import { useState, useMemo } from 'react'
import { Box, TextField, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { JsonView } from '@uiw/react-json-view'
import { darkTheme } from '@uiw/react-json-view/dark'
import { lightTheme } from '@uiw/react-json-view/light'
import { JObject } from '../types/Constants'

interface JsonViewerProps {
  documents: JObject[]
}

type JValue = unknown

function filterByPropertyName(value: JValue, filter: string, parentKeyMatched = false): JValue {
  if (Array.isArray(value)) {
    if (parentKeyMatched) return value
    const filtered = value
      .map((item) => filterByPropertyName(item as JValue, filter, false))
      .filter((item) => item !== undefined)
    return filtered.length > 0 ? filtered : undefined
  }
  if (value === null || typeof value !== 'object') {
    return parentKeyMatched ? value : undefined
  }
  if (parentKeyMatched) return value

  const result: Record<string, JValue> = {}
  let hasMatch = false
  for (const [key, child] of Object.entries(value)) {
    const keyMatches = key.toLowerCase().includes(filter)
    const filtered = filterByPropertyName(child as JValue, filter, keyMatches)
    if (filtered !== undefined) {
      result[key] = filtered
      hasMatch = true
    } else if (keyMatches) {
      result[key] = child as JValue
      hasMatch = true
    }
  }
  return hasMatch ? result : undefined
}

const JsonViewer = ({ documents }: JsonViewerProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [propertyFilter, setPropertyFilter] = useState('')
  const [selectedDocIndex, setSelectedDocIndex] = useState(0)

  const currentDoc = documents[selectedDocIndex] ?? null

  const filteredJson = useMemo(() => {
    if (!currentDoc) return null
    if (!propertyFilter.trim()) return currentDoc as JValue
    const filtered = filterByPropertyName(currentDoc as JValue, propertyFilter.toLowerCase())
    return filtered ?? null
  }, [currentDoc, propertyFilter])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('investigation.data.filterProperty')}
          value={propertyFilter}
          onChange={(e) => setPropertyFilter(e.target.value)}
          sx={{ width: 300 }}
        />
        {documents.length > 1 && (
          <Typography variant="body2" color="text.secondary">
            {t('investigation.data.documentCount', { count: documents.length })}
            {' — '}
            <select
              value={selectedDocIndex}
              onChange={(e) => setSelectedDocIndex(Number(e.target.value))}
              style={{ fontSize: 'inherit' }}
            >
              {documents.map((doc, i) => (
                <option key={i} value={i}>
                  {doc.id ?? `#${i + 1}`}
                </option>
              ))}
            </select>
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: isDark ? 'grey.900' : 'grey.50',
          borderRadius: 1,
          p: 2,
          fontSize: '0.85rem',
        }}
      >
        {filteredJson ? (
          <JsonView
            value={filteredJson}
            collapsed={2}
            shortenTextAfterLength={0}
            displayDataTypes={false}
            enableClipboard={false}
            style={{
              ...(isDark ? darkTheme : lightTheme),
              backgroundColor: 'transparent',
              fontFamily: 'monospace',
            }}
          />
        ) : (
          <Typography>{t('investigation.data.noDocuments')}</Typography>
        )}
      </Box>
    </Box>
  )
}

export default JsonViewer
